<script setup lang="ts">
import { DeviceDetailPage } from '@/components/DeviceComponents'
import DeviceAssignCheckpointAction from '@/components/DeviceComponents/components/DeviceAssignCheckpointAction.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import deviceApi from '@/services/device.service'
import deviceConfig from '@/services/deviceConfig.service'
import store from '@/store'
import { EAlertActions } from '@/store/alertStore/AlertStoreTypes'
import { EDeviceActions } from '@/store/deviceStore/DeviceStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { EDeviceStatus } from '@/types/enum/DeviceStatusEnum'
import { defaultCreateDevice } from '@/types/models/DeviceCreateModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { DeviceTemplateKeysModel } from '@/types/models/DeviceTemplateKeysModel'
import { EOrganizationsDevicesPerm } from '@/types/permissions/OrganizationsPermissionsEnum'
import { ERentalsPerm } from '@/types/permissions/RentalsPermissionsEnum'
import { addToQuery } from '@/utils/queryUtils'
import { isEqual } from 'lodash'
import moment from 'moment'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import GridLayout from '../../components/Layouts/GridLayout.vue'
import type { ExtendedPrimitives } from '../../types/ExtendedPrimitives'
import { EDetectionsPerm } from '../../types/permissions/DetectionsPermissionsEnum'
import DeviceAddDetectionAction from './components/DeviceAddDetectionAction.vue'
import DeviceCreateRentalAction from './components/DeviceCreateRentalAction.vue'
import DeviceEndRentalAction from './components/DeviceEndRentalAction.vue'
import DeviceUpdateRentalAction from './components/DeviceUpdateRentalAction.vue'

type Props = {
    device?: Partial<DeviceModel>
    tabsMultilined?: boolean
    isOutlookMode?: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh', device?: Partial<DeviceModel>): void
}
const emit = defineEmits<Emits>()

const { t } = useI18n()

const route = useRoute()
const deviceStore = ref({ ...defaultCreateDevice })
const organizationId = computed(() => store.state.authentication.me.organization_id)

const localDeviceId = computed(() => {
    if (route.query.preview) return parseInt(route.query.preview.toString())
    if (props.device && props.device.id) return props.device.device_id ?? props.device.id
    return route.params.device_id ? parseInt(route.params.device_id.toString()) : undefined
})

const loading = ref(false)

const data = ref<Partial<DeviceModel>>({ ...defaultCreateDevice })
const templateKeys = ref<DeviceTemplateKeysModel[]>([])
const getDeviceConfig = (deviceId: number) =>
    deviceConfig
        .getDeviceTypeConfigByVersion(deviceId)
        .then(res => (templateKeys.value = res.data?.properties ?? []))

const getDevice = (avoidRefreshQuery?: boolean) => {
    loading.value = true
    store
        .dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.FETCH_API_DEVICE}`, {
            noDocumentTitle: props.isOutlookMode,
            deviceId: localDeviceId.value
        })
        .then((res: DeviceModel) => {
            data.value = { ...res }
            deviceStore.value = { ...res }
            if (!avoidRefreshQuery && props.isOutlookMode)
                addToQuery({ preview: res.device_id ?? res.id })
            if (res.device_type_id && localDeviceId.value)
                return getDeviceConfig(localDeviceId.value)
            templateKeys.value = []
        })
        .finally(() => (loading.value = false))
}

watch(
    () => localDeviceId.value,
    (newDeviceId, oldDeviceId) => {
        if (newDeviceId && newDeviceId !== oldDeviceId) getDevice()
    },
    { immediate: true }
)

const save = () => {
    if (!localDeviceId.value) return
    if (!isEqual(data.value, deviceStore.value)) {
        loading.value = true
        deviceApi
            .updateDeviceDevice(localDeviceId.value, {
                ...data.value,
                config: data.value.config
            })
            .then(res => {
                data.value = { ...data.value, ...res.data }
                deviceStore.value = { ...deviceStore.value, ...res.data }
                store.dispatch(`${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`, {
                    action: EAlertActions.SUCCESS,
                    message: t(`device.api.success.${EDeviceActions.UPDATE_DEVICE}`, {
                        name: data.value.name
                    })
                })
            })
            .finally(() => {
                loading.value = false
            })
    }
    // TODO : dialog success
}

const updateDeviceValue = (value: Partial<DeviceModel>) =>
    (data.value = { ...data.value, ...value })

const deviceStatus = computed(() => ({
    battery: props.device?.battery ?? deviceStore.value?.battery,
    plugged: props.device?.plugged ?? deviceStore.value?.plugged,
    signal: props.device?.signal ?? deviceStore.value?.signal,
    status: (props.device?.status as EDeviceStatus) ?? (deviceStore.value?.status as EDeviceStatus),
    pingtime: props.device?.pingtime ?? deviceStore.value?.pingtime,
    network_type: props.device?.network_type ?? deviceStore.value?.network_type
}))

const handleRefresh = (value?: Partial<DeviceModel>) => {
    getDevice(true)
    emit('refresh', value)
}

const currentlyRent = computed(
    () =>
        data.value?.rental?.start_at &&
        data.value?.rental?.end_at &&
        moment().isAfter(data.value?.rental.start_at) &&
        moment().isBefore(data.value?.rental?.end_at)
)

const currentlyRentAndOwner = computed(
    () =>
        !!localDeviceId.value &&
        !!deviceStore.value?.rental?.id &&
        currentlyRent.value &&
        deviceStore.value?.rental?.renter?.organization_id !== organizationId.value
)

const currentlyRentAndRenter = computed(
    () =>
        !!localDeviceId.value &&
        !!deviceStore.value?.rental?.id &&
        currentlyRent.value &&
        deviceStore.value?.rental?.renter?.organization_id === organizationId.value
)

const updateDeviceConfig = (config: Record<string, ExtendedPrimitives>) => {
    const deviceStoreConfig = { ...deviceStore.value.config }
    const dataConfig = { ...data.value.config }

    const configDifferences = Object.entries(dataConfig ?? {}).reduce(
        (prev, [itemKey, itemValue]) =>
            deviceStoreConfig[itemKey] === itemValue
                ? prev
                : { ...prev, [itemKey]: dataConfig[itemKey] ?? itemValue },
        {}
    )

    data.value.config = { ...dataConfig, ...config, ...configDifferences }
    deviceStore.value.config = { ...deviceStore.value.config, ...config }
}
</script>

<template>
    <DeviceDetailPage
        :storedData="deviceStore"
        :data="data"
        :status="deviceStatus"
        :templateKeys="templateKeys"
        :editPermission="EOrganizationsDevicesPerm.ORGANIZATIONS_DEVICES_EDIT"
        :tabsMultilined="tabsMultilined"
        @save="save"
        @update="updateDeviceValue"
        @refresh="getDevice"
        @configUpdate="updateDeviceConfig"
        :loading="loading">
        <template #actions>
            <GridLayout :maxItemPerRow="2" :gap="8" class="full-width" isResponsive>
                <DeviceAssignCheckpointAction
                    :device="device ?? deviceStore ?? data"
                    @refresh="handleRefresh" />
                <PermissionWrapper :permission="ERentalsPerm.RENTALS_EDIT">
                    <DeviceCreateRentalAction
                        v-if="!!localDeviceId && device && !currentlyRent"
                        :deviceId="localDeviceId"
                        @refresh="handleRefresh(device ?? deviceStore ?? data)" />
                    <DeviceUpdateRentalAction
                        v-if="!!currentlyRentAndOwner && !!deviceStore?.rental"
                        :data="{
                            start_at: deviceStore.rental.start_at,
                            end_at: deviceStore.rental.end_at
                        }"
                        :rentalId="deviceStore.rental.id"
                        @refresh="handleRefresh(device ?? deviceStore ?? data)" />
                    <DeviceEndRentalAction
                        v-if="!!currentlyRentAndOwner && !!deviceStore?.rental"
                        :rentalId="deviceStore.rental.id"
                        @refresh="handleRefresh(device ?? deviceStore ?? data)" />
                </PermissionWrapper>
                <PermissionWrapper :permission="EDetectionsPerm.DETECTIONS_EDIT">
                    <DeviceAddDetectionAction
                        v-if="!!currentlyRentAndRenter || !currentlyRent"
                        :deviceId="localDeviceId"
                        :event="device?.event ?? data?.event"
                        :disabled="!device?.checkpoint?.id && !data.checkpoint?.id"
                        @refresh="handleRefresh"
                        class="full-width" />
                </PermissionWrapper>
            </GridLayout>
        </template>
    </DeviceDetailPage>
</template>
